import { useState, useRef } from "react";
import { FiEye, FiEyeOff, FiLock, FiShield } from "react-icons/fi";


import api from "../../api/Api";
import { useTheme } from "../../context/ThemeContext";
import { toast } from "react-toastify";
import { useUser } from "../../context/UserContext";

/* ── Password strength helper ───────────────────────────────────────────── */
function getStrength(pwd) {
  if (!pwd) return 0;
  let s = 0;
  if (pwd.length >= 6)  s++;
  if (pwd.length >= 10) s++;
  if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) s++;
  if (/[^a-zA-Z0-9]/.test(pwd)) s++;
  return Math.min(s, 4);
}
const STRENGTH_LABELS = ["", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLORS = ["", "#e63946", "#f4a261", "#d4af37", "#2dc653"];

/* ── EyeInput ────────────────────────────────────────────────────────────── */
function EyeInput({ id, value, onChange, placeholder, mono = false, S }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          ...S.input,
          fontFamily: mono ? "monospace" : "inherit",
          letterSpacing: mono ? "0.15em" : "normal",
        }}
        onFocus={e => {
          e.target.style.borderColor = "#d4af37";
          e.target.style.boxShadow   = "0 0 0 2px rgba(212,175,55,.18)";
        }}
        onBlur={e => {
          e.target.style.borderColor = S._bdr;
          e.target.style.boxShadow   = "none";
        }}
      />
      <button
        type="button"
        onClick={() => setShow(p => !p)}
        style={S.eyeBtn}
        aria-label="Toggle password visibility"
      >
        {show ? <FiEyeOff size={16} /> : <FiEye size={16} />}
      </button>
    </div>
  );
}

/* ── StrengthBar ─────────────────────────────────────────────────────────── */
function StrengthBar({ pwd, bdr }) {
  const s = getStrength(pwd);
  return (
    <>
      <div style={{ display: "flex", gap: 3}}>
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            style={{
              height: 3, flex: 1, borderRadius: 2,
              background: i <= s ? STRENGTH_COLORS[s] : bdr,
              transition: "background .25s",
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: 11, color: "#9c8c70", marginTop: 4, minHeight: 14 }}>
        {pwd ? STRENGTH_LABELS[s] : ""}
      </div>
    </>
  );
}

/* ── Spinner ─────────────────────────────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("__pwd-spin")) {
  const st = document.createElement("style");
  st.id = "__pwd-spin";
  st.textContent = "@keyframes spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(st);
}
function Spinner() {
  return (
    <div style={{
      width: 16, height: 16, flexShrink: 0,
      border: "2px solid rgba(26,21,0,.3)",
      borderTopColor: "#1a1500", borderRadius: "50%",
      animation: "spin .65s linear infinite",
    }} />
  );
}

/* ── Main Component ──────────────────────────────────────────────────────── */
export default function SettingPasswors() {
  const { isDarkMode } = useTheme();
  const {user} = useUser()
  const U = user?.USER ?? user ?? {};

  /* ── theme tokens (your exact variables) ───────────────────────────── */
  const G         = "#d4af37";
  const G2        = "#f0c842";
  const card      = isDarkMode ? "#1a1814" : "#ffffff";
  const bdr       = isDarkMode ? "rgba(212,175,55,.14)" : "rgba(180,155,100,.22)";
  const muted     = isDarkMode ? "#7a7060" : "#9c8c70";
  const text      = isDarkMode ? "#f0ece2" : "#2c2416";
  const inpBg     = isDarkMode ? "#12110e" : "rgb(245,241,232)";
  const surfaceBg = isDarkMode ? "#1f1c17" : "#f5f1e8";

  /* ── shared style objects ───────────────────────────────────────────── */
  const S = {
    _bdr: bdr, // for onBlur reference inside EyeInput
    card: {
      background: card,
      border: `0.5px solid ${bdr}`,
      borderRadius: 12,
      overflow: "hidden",
      marginBottom: 16,
      
      transition: "background .2s, border-color .2s",
    },
    cardHead: {
      display: "flex", alignItems: "center", gap: 10,
      padding: "14px 20px",
      borderBottom: `0.5px solid ${bdr}`,
      background: surfaceBg,
    },
    headIcon: {
      width: 36, height: 36, borderRadius: 8,
      background: "rgba(212,175,55,.13)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, color: G,
    },
    headTitle : { fontSize: 15, fontWeight: 500, color: text },
    headSub   : { fontSize: 12, color: muted, marginTop: 2 },
    body      : { padding: 20 },
    field     : { marginBottom: 16 },
    label: {
      display: "block", fontSize: 11, fontWeight: 500,
      color: muted, letterSpacing: "0.06em",
      textTransform: "uppercase", marginBottom: 6,
    },
    input: {
      width: "100%", padding: "11px 40px 11px 13px",
      background: inpBg, border: `0.5px solid ${bdr}`,
      borderRadius: 8, fontSize: 14, color: text, outline: "none",
      transition: "border-color .18s, box-shadow .18s",
      fontFamily: "inherit",
    },
    eyeBtn: {
      position: "absolute", right: 11, background: "none",
      border: "none", cursor: "pointer", color: muted,
      display: "flex", padding: 3,
    },
    sep: { height: "0.5px", background: bdr, margin: "16px 0" },
    submit: {
      width: "100%", marginTop: 18, padding: "12px",
      background: G, color: "#1a1500", border: "none",
      borderRadius: 8, fontSize: 14, fontWeight: 500,
      cursor: "pointer", display: "flex",
      alignItems: "center", justifyContent: "center", gap: 7,
      transition: "background .18s, transform .18s",
      letterSpacing: "0.01em",
    },
    submitDisabled: {
      opacity: 0.55, cursor: "not-allowed",
    },
    otpSend: {
      flexShrink: 0, padding: "11px 13px",
      background: inpBg, border: `0.5px solid ${bdr}`,
      borderRadius: 8, fontSize: 12, fontWeight: 500,
      color: G, cursor: "pointer", whiteSpace: "nowrap",
      transition: "background .18s, border-color .18s",
      fontFamily: "inherit",
    },
  };

  /* ── login password state ───────────────────────────────────────────── */
  const [oldPwd,     setOldPwd]     = useState("");
  const [newPwd,     setNewPwd]     = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);

  /* ── txn password state ─────────────────────────────────────────────── */
  const [txnNewPwd,  setTxnNewPwd]  = useState("");
  const [otp,        setOtp]        = useState("");
  const [txnLoading, setTxnLoading] = useState(false);

  /* ── OTP state ──────────────────────────────────────────────────────── */
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const cooldownRef = useRef(null);

  /* ── Update Login Password ──────────────────────────────────────────── */
  const handleUpdatePassword = async () => {
    if (!oldPwd || !newPwd) return toast.error("Please fill in all fields.");
    if (newPwd.length < 6)  return toast.error("New password must be at least 6 characters.");
    setPwdLoading(true);
    try {
      const fd = new FormData();
      fd.append("old_password", oldPwd);
      fd.append("new_password", newPwd);
      const res = await api.post("updatePassword", fd);
      toast.success(res.data?.message || "Password updated successfully!");
      setOldPwd(""); setNewPwd("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update password.");
    } finally {
      setPwdLoading(false);
    }
  };

  /* ── Send OTP ───────────────────────────────────────────────────────── */
  const handleSendOtp = async () => {
    setOtpLoading(true);
    try {
      const res = await api.post("/request-email-otp", { email: U.email });
      toast.success(res.data?.message || "OTP sent to your registered contact.");
      setOtpSent(true);
      setOtpCooldown(60);
      cooldownRef.current = setInterval(() => {
        setOtpCooldown(c => {
          if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  /* ── Update Transaction Password ────────────────────────────────────── */
  const handleUpdateTxnPassword = async () => {
    if (!txnNewPwd || !otp) return toast.error("Please fill all fields including OTP.");
    if (txnNewPwd.length < 6) return toast.error("Password must be at least 6 characters.");
    if (otp.length !== 6)     return toast.error("Enter a valid 6-digit OTP.");
    setTxnLoading(true);
    try {
      const fd = new FormData();
      fd.append("new_password", txnNewPwd);
      fd.append("otp", otp);
      const res = await api.post("updateTransactionPassword", fd);
      toast.success(res.data?.message || "Transaction password updated successfully!");
      setTxnNewPwd(""); setOtp("");
      setOtpSent(false); setOtpCooldown(0);
      clearInterval(cooldownRef.current);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update transaction password.");
    } finally {
      setTxnLoading(false);
    }
  };

  /* ── derived flags ──────────────────────────────────────────────────── */
  const loginDisabled  = !oldPwd || !newPwd || pwdLoading;
  const txnDisabled    = !txnNewPwd || otp.length !== 6 || txnLoading;
  const otpBtnDisabled = otpLoading || otpCooldown > 0;

  /* ── OTP button label ───────────────────────────────────────────────── */
  const otpBtnLabel = otpLoading
    ? "Sending…"
    : otpCooldown > 0
      ? `Resend (${otpCooldown}s)`
      : otpSent ? "Resend OTP" : "Send OTP";

  return (
    <div     style={{
        width: '100%',
        borderRadius: 28,
        background: card,
        boxShadow: isDarkMode
          ? '0 12px 35px rgba(0,0,0,.30)'
          : '0 12px 35px rgba(0,0,0,.05)',
      }}>

      {/* ───────────────── Card 1: Login Password ─────────────────────── */}
      <div style={S.card}>
        <div style={S.cardHead}>
          <div style={S.headIcon}><FiLock size={17} /></div>
          <div>
            <div style={S.headTitle}>Login password</div>
            <div style={S.headSub}>Update your account login credentials</div>
          </div>
        </div>

        <div style={S.body}>
          {/* Current password */}
          <div style={S.field}>
            <label style={S.label} htmlFor="oldPwd">Current password</label>
            <EyeInput
              id="oldPwd"
              value={oldPwd}
              onChange={e => setOldPwd(e.target.value)}
              placeholder="Enter current password"
              S={S}
            />
          </div>

          {/* New password + strength */}
          <div style={S.field}>
            <label style={S.label} htmlFor="newPwd">New password</label>
            <EyeInput
              id="newPwd"
              value={newPwd}
              onChange={e => setNewPwd(e.target.value)}
              placeholder="Min. 6 characters"
              S={S}
            />
            <StrengthBar pwd={newPwd} bdr={bdr} />
          </div>

          <div style={S.sep} />

          <button
            style={{
              ...S.submit,
              ...(loginDisabled ? S.submitDisabled : {}),
            }}
            onClick={handleUpdatePassword}
            disabled={loginDisabled}
          >
            {pwdLoading && <Spinner />}
            {pwdLoading ? "Updating…" : "Update login password"}
          </button>
        </div>
      </div>

      {/* ───────────────── Card 2: Transaction Password ───────────────── */}
      <div style={S.card}>
        <div style={S.cardHead}>
          <div style={S.headIcon}><FiShield size={17} /></div>
          <div>
            <div style={S.headTitle}>Transaction password</div>
            <div style={S.headSub}>OTP required to update</div>
          </div>
        </div>

        <div style={S.body}>
          {/* New txn password */}
          <div style={S.field}>
            <label style={S.label} htmlFor="txnPwd">New transaction password</label>
            <EyeInput
              id="txnPwd"
              value={txnNewPwd}
              onChange={e => setTxnNewPwd(e.target.value)}
              placeholder="Min. 6 characters"
              S={S}
            />
          </div>

          {/* OTP row */}
          <div style={S.field}>
            <label style={S.label}>OTP verification</label>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              {/* OTP input */}
              <div style={{ flex: 1 }}>
                <input
                  type="tel"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit OTP"
                  maxLength={6}
                  style={{
                    ...S.input,
                    fontFamily: "monospace",
                    letterSpacing: "0.15em",
                    fontSize: 15,
                    padding: "11px 13px",
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = G;
                    e.target.style.boxShadow   = "0 0 0 2px rgba(212,175,55,.18)";
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = bdr;
                    e.target.style.boxShadow   = "none";
                  }}
                />
              </div>

              {/* Send OTP button */}
              <button
                style={{
                  ...S.otpSend,
                  ...(otpBtnDisabled ? { opacity: 0.55, cursor: "not-allowed" } : {}),
                }}
                onClick={handleSendOtp}
                disabled={otpBtnDisabled}
              >
                {otpBtnLabel}
              </button>
            </div>

            {/* OTP sent note */}
            {otpSent && (
              <div style={{
                fontSize: 11, color: G, marginTop: 5,
                display: "flex", alignItems: "center", gap: 5,
              }}>
                <FiShield size={12} />
                {otpCooldown > 0
                  ? `OTP sent — resend in ${otpCooldown}s`
                  : "OTP sent — you can resend now"}
              </div>
            )}
          </div>

          <div style={S.sep} />

          <button
            style={{
              ...S.submit,
              ...(txnDisabled ? S.submitDisabled : {}),
            }}
            onClick={handleUpdateTxnPassword}
            disabled={txnDisabled}
          >
            {txnLoading && <Spinner />}
            {txnLoading ? "Updating…" : "Update transaction password"}
          </button>
        </div>
      </div>
    </div>
  );
}